<script>
import Money from "@/src/mixins/money";
import WarningIcon from "@src/components/ui-design/icons/WarningIcon.vue";

export default {
  mixins: [Money],
  emits: ["submit", "hidden"],
  components: {
    WarningIcon,
  },
  props: {
    id: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    alert: {
      type: Object,
      default: () => ({
        title: "",
        description: "",
      }),
    },
    sale: {
      type: Object,
      required: true,
    },
  },
  computed: {
    confirmationLabel() {
      return this.confirmationType[this.type] || this.type.toUpperCase();
    },
    saleClientName() {
      return this.sale && this.sale.client && this.sale.client.name
        ? this.sale.client.name
        : "-";
    },
    saleId() {
      return this.sale && this.sale.id !== null && this.sale.id !== undefined
        ? this.sale.id
        : "-";
    },
    saleTotal() {
      const total = this.sale ? this.sale.total : null;

      if (total === null || total === undefined) {
        return "-";
      }

      return total === 0 ? "R$ 0,00" : `R$ ${this.formatMoney(total)}`;
    },
    isDisabled() {
      if (this.step === 2 && this.confirmationText !== this.confirmationLabel) {
        return true;
      }

      return false;
    },
    submitLabel() {
      if (this.step !== 2) {
        return "Avançar";
      }

      return this.type === "email" ? "Alterar E-mail" : this.confirmationLabel;
    },
  },
  data() {
    return {
      step: 1,
      confirmationText: "",
      confirmationType: {
        email: "ALTERAR E-MAIL",
      },
    };
  },
  methods: {
    handleSubmit(step) {
      if (this.loading) {
        return;
      }

      if (step !== 3) {
        this.step = step;
      }

      this.$emit("submit", step);
    },
    handleHidden() {
      this.step = 1;
      this.confirmationText = "";
      this.$emit("hidden");
    },
  },
};
</script>

<template>
  <b-modal
    centered
    size="md"
    :id="id"
    dialog-class="actions-modal__dialog"
    :content-class="[
      'actions-modal__content',
      { 'actions-modal__content--confirmation': step === 2 },
    ]"
    body-class="actions-modal__body"
    hide-header
    hide-footer
    :no-close-on-backdrop="loading"
    :no-close-on-esc="loading"
    @hidden="handleHidden"
  >
    <div class="actions-modal">
      <div class="actions-modal__header">
        <div>
          <h6 class="details__subtitle actions-modal__title">
            {{ title }}
          </h6>
          <p>{{ description }}</p>
        </div>
        <button
          type="button"
          class="actions-modal__close"
          aria-label="Fechar modal"
          :disabled="loading"
          @click="$bvModal.hide(id)"
        >
          <i class="mdi mdi-close"></i>
        </button>
      </div>
      <div class="actions-modal__alert" v-if="step === 1">
        <WarningIcon class="actions-modal__alert-icon" />
        <p class="mb-0">
          {{ alert.description }}
        </p>
      </div>
      <b-alert v-if="step === 2 && errorMessage" show variant="danger">
        {{ errorMessage }}
      </b-alert>
      <form @submit.prevent="handleSubmit(step + 1)">
        <fieldset v-if="step === 2" :disabled="loading">
          <div class="actions-modal__summary">
            <ul class="m-0 p-0">
              <li class="d-flex justify-content-between">
                <p>Cliente:</p>
                <p>{{ saleClientName }}</p>
              </li>
              <li class="d-flex justify-content-between">
                <p :class="!['refund', 'cancel'].includes(type) ? 'mb-0' : ''">
                  ID da Venda:
                </p>
                <p
                  :class="!['refund', 'cancel'].includes(type) ? 'mb-0' : ''"
                >
                  {{ saleId }}
                </p>
              </li>
              <li
                v-if="['refund', 'cancel'].includes(type)"
                class="d-flex justify-content-between"
              >
                <p>Valor:</p>
                <p>{{ saleTotal }}</p>
              </li>
            </ul>
          </div>

          <slot />

          <div>
            <div>
              <label class="actions-modal__confirmation-label">
                <p class="mb-1">
                  Para confirmar, digite
                  <span>{{ confirmationLabel }}</span>
                  abaixo
                </p>
                <b-form-input
                  v-model="confirmationText"
                  type="text"
                  :placeholder="`Digite ${confirmationLabel}.`"
                  class="form-control mb-3"
                  required
                />
              </label>
            </div>
          </div>
        </fieldset>
        <div class="actions-modal__cta">
          <button
            class="actions-modal__button actions-modal__button--close"
            type="button"
            :disabled="loading"
            @click="$bvModal.hide(id)"
          >
            Cancelar
          </button>
          <button
            class="actions-modal__button actions-modal__button--submit"
            type="submit"
            :disabled="isDisabled || loading"
          >
            {{ submitLabel }}
          </button>
        </div>
      </form>
      <div v-if="loading" class="actions-modal__loading">
        <div class="spinner-border" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss">
.actions-modal__dialog {
  max-width: 512px;
}

.actions-modal__content {
  min-height: 316px;
  overflow: hidden;
  border: 0;
  border-radius: 8px;
  background: #fff;

  &--confirmation {
    min-height: 642px;

    .actions-modal__body {
      display: flex;
    }

    .actions-modal {
      display: flex;
      flex: 1;
      flex-direction: column;
    }

    form {
      display: flex;
      flex: 1;
      flex-direction: column;
    }

    .actions-modal__cta {
      margin-top: auto;
    }
  }
}

.actions-modal__body {
  min-height: 316px;
  padding: 24px 24px 32px;
  position: relative;
}

.actions-modal {
  height: 100%;
  width: 100%;
  color: #000c0b;

  &__header {
    padding-right: 36px;

    p {
      color: #626b69;
      font-size: 16px;
      line-height: 24px;
      margin: 0 0 32px;
    }
  }

  &__close {
    appearance: none;
    background: #f0f2f5;
    border: 0;
    padding: 4px;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: absolute;
    right: 20px;
    top: 16px;

    i {
      color: #999e9d;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 10px;
    }
  }

  &__title {
    font-size: 20px;
    line-height: 28px;
    margin-top: 0;
    margin-bottom: 12px;
    color: #000c0b;
    font-weight: 700;
  }

  &__loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    border-radius: 8px;
  }

  &__alert {
    border-radius: 8px;
    background: #fefce8;
    border: 1px solid #9e610a;
    color: #9e610a;
    line-height: 24px;
    padding: 16px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 32px;
    min-height: 80px;

    p {
      color: #9e610a;
      font-size: 16px;
      line-height: 24px;
      margin: 0;
    }
  }

  &__alert-icon {
    flex: 0 0 auto;
    margin-top: 3px;
  }

  &__summary {
    background: #f7f7f7;
    border-radius: 8px;
    color: #000c0b;
    margin-bottom: 24px;
    padding: 16px 20px;

    p {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      margin: 0;
    }

    li + li {
      margin-top: 12px;
    }

    p:first-child {
      color: #626b69;
    }

    p:last-child {
      color: #000c0b;
    }
  }

  &__confirmation-label {
    color: #000c0b;
    width: 100%;

    span {
      background: #f0f2f5;
      border-radius: 8px;
      font-weight: 600;
      padding: 2px 4px;
    }

    .form-control {
      box-sizing: border-box;
      height: 48px;
      margin-top: 8px;
    }
  }

  &__cta {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
  }

  &__button {
    appearance: none;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }

    &--close {
      color: #000c0b;
      background: #f0f2f5;
      border: 0;
      border-radius: 8px;
      height: 46px;
      min-width: 98px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
    }

    &--submit {
      background-color: #000c0b;
      border: 0;
      border-radius: 8px;
      color: #fff !important;
      height: 46px;
      min-width: 128px;
      padding: 0 20px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
    }
  }
}
</style>
